import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { SharedService } from '@shared/service/sharedservice';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate, CanActivateChild {
  constructor(private sharedService: SharedService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.validateAccess(route);
  }

  canActivateChild( childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot ): boolean | UrlTree {
    return this.validateAccess(childRoute);
  }

  private validateAccess(route: ActivatedRouteSnapshot): boolean | UrlTree {
    const allowedRoles = this.collectAllowedRoles(route);
    if (allowedRoles.length === 0) {
      return true;
    }

    const userRoles = this.sharedService.getUserRoles();
    const normalizedAllowed = allowedRoles.map(role => role.toLowerCase());
    const matchingRole = userRoles.find(role => normalizedAllowed.includes((role.name ?? '').toLowerCase()));

    if (matchingRole) {
      const activeRole = this.sharedService.getActiveRole();
      if ((activeRole ?? '').toLowerCase() !== (matchingRole.name ?? '').toLowerCase()) {
        this.sharedService.setCurrentRole(matchingRole.name);
      }
      return true;
    }

    return this.redirectToDefault();
  }

  private collectAllowedRoles(route: ActivatedRouteSnapshot): string[] {
    const roles = route.data?.['allowedRoles'];
    if (Array.isArray(roles) && roles.length > 0) {
      return roles;
    }
    if (route.parent) {
      return this.collectAllowedRoles(route.parent);
    }
    return [];
  }

  private redirectToDefault(): UrlTree {
    const activeRole = this.sharedService.getActiveRole();
    const defaultRoute = this.sharedService.getDefaultRouteForRole(activeRole);
    const commands = this.sharedService.buildRoleBasedSegments(...defaultRoute.segments);
    const extras = defaultRoute.queryParams ? { queryParams: defaultRoute.queryParams } : undefined;
    return this.router.createUrlTree(commands, extras);
  }
}